import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, User, FileText, KeyRound, Fingerprint, Phone, Mail, Globe } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Branding
const LOGO_URL = "/logo.jpg";
const PRACTICE_NAME = "Protea Health Ltd";
const PRACTICE_WEBSITE = "https://adhdclinicjersey.proteahealth.co.uk";
const PRACTICE_PHONE = "01534 484191";
const PRACTICE_EMAIL = "docjack@proteahealth.co.uk";

// WebAuthn helper functions
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64urlToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLen);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

export default function StartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token") || "";
  
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(urlToken ? "token" : "details");
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [pendingPatientData, setPendingPatientData] = useState(null);
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  
  // Form state for name/DOB
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [email, setEmail] = useState("");
  
  // Form state for token
  const [token, setToken] = useState(urlToken);
  const [tokenEmail, setTokenEmail] = useState("");

  useEffect(() => {
    // Check if WebAuthn is supported
    setIsPasskeySupported(
      window.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === 'function'
    );
  }, []);

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !dobDay || !dobMonth || !dobYear || !email) {
      toast.error("Please fill in all fields");
      return;
    }
    
    const dateOfBirth = `${dobYear}-${dobMonth}-${dobDay}`;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/patients/identify`, {
        first_name: firstName,
        last_name: lastName,
        date_of_birth: dateOfBirth,
        email: email
      });
      
      if (response.data.verified) {
        const patientData = {
          patient_id: response.data.patient_id,
          first_name: firstName,
          last_name: lastName,
          email: email
        };
        
        // Check if passkey is supported and user doesn't have one yet
        if (isPasskeySupported) {
          try {
            const passkeyCheck = await axios.get(`${API}/passkey/check/${encodeURIComponent(email)}`);
            if (!passkeyCheck.data.has_passkey) {
              setPendingPatientData(patientData);
              setShowPasskeyPrompt(true);
              setIsLoading(false);
              return;
            }
          } catch (e) {
            // Continue without passkey prompt if check fails
          }
        }
        
        sessionStorage.setItem("patientData", JSON.stringify(patientData));
        toast.success("Identity verified");
        navigate("/payment");
      }
    } catch (error) {
      console.error("Identification error:", error);
      toast.error(error.response?.data?.detail || "Failed to verify identity. Please check your details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    
    if (!token || !tokenEmail) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/patients/identify-token`, {
        token: token,
        email: tokenEmail
      });
      
      if (response.data.verified) {
        const patientData = {
          patient_id: response.data.patient_id,
          first_name: response.data.first_name || "",
          last_name: response.data.last_name || "",
          email: tokenEmail
        };
        
        // Check if passkey is supported and user doesn't have one yet
        if (isPasskeySupported) {
          try {
            const passkeyCheck = await axios.get(`${API}/passkey/check/${encodeURIComponent(tokenEmail)}`);
            if (!passkeyCheck.data.has_passkey) {
              setPendingPatientData(patientData);
              setShowPasskeyPrompt(true);
              setIsLoading(false);
              return;
            }
          } catch (e) {
            // Continue without passkey prompt if check fails
          }
        }
        
        sessionStorage.setItem("patientData", JSON.stringify(patientData));
        toast.success("Identity verified");
        navigate("/payment");
      }
    } catch (error) {
      console.error("Token identification error:", error);
      toast.error(error.response?.data?.detail || "Invalid token. Please check and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyRegister = async () => {
    if (!pendingPatientData) return;
    
    setIsLoading(true);
    
    // Auto-skip timeout - if no response in 10 seconds, skip
    const autoSkipTimeout = setTimeout(() => {
      console.log("Passkey registration timed out, auto-skipping");
      toast.info("Passkey setup timed out. Continuing without it.");
      sessionStorage.setItem("patientData", JSON.stringify(pendingPatientData));
      setShowPasskeyPrompt(false);
      setIsLoading(false);
      navigate("/payment");
    }, 10000);
    
    try {
      // Check if WebAuthn is available
      if (!window.PublicKeyCredential) {
        clearTimeout(autoSkipTimeout);
        toast.error("Passkeys not supported in this browser");
        handleSkipPasskey();
        return;
      }
      
      // Start registration
      const startRes = await axios.post(`${API}/passkey/register/start`, {
        patient_id: pendingPatientData.patient_id,
        email: pendingPatientData.email
      });
      
      const { challenge, rp_id, rp_name, user_id, user_name, user_display_name } = startRes.data;
      
      // Build credential creation options - simplified for Safari/Mac compatibility
      const publicKeyCredentialCreationOptions = {
        challenge: base64urlToBuffer(challenge),
        rp: {
          name: rp_name,
          id: rp_id
        },
        user: {
          id: new TextEncoder().encode(user_id),
          name: user_name,
          displayName: user_display_name || user_name
        },
        pubKeyCredParams: [
          { type: "public-key", alg: -7 },   // ES256 (used by Apple)
          { type: "public-key", alg: -257 }, // RS256
        ],
        timeout: 60000,
        attestation: "none"
        // Removed authenticatorSelection to let Safari choose the best option
      };
      
      console.log("Creating passkey credential...");
      
      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });
      
      clearTimeout(autoSkipTimeout);
      
      if (!credential) {
        throw new Error("No credential returned from authenticator");
      }
      
      console.log("Credential created successfully");
      
      // Get public key
      let publicKeyBase64 = "";
      try {
        if (credential.response.getPublicKey && typeof credential.response.getPublicKey === 'function') {
          const pubKey = credential.response.getPublicKey();
          if (pubKey) {
            publicKeyBase64 = bufferToBase64url(pubKey);
          }
        }
      } catch (e) {
        console.log("Using attestationObject instead");
      }
      
      if (!publicKeyBase64 && credential.response.attestationObject) {
        publicKeyBase64 = bufferToBase64url(credential.response.attestationObject);
      }
      
      // Finish registration
      await axios.post(`${API}/passkey/register/finish`, {
        patient_id: pendingPatientData.patient_id,
        credential_id: bufferToBase64url(credential.rawId),
        public_key: publicKeyBase64,
        challenge: challenge
      });
      
      toast.success("Passkey saved! You can use it for faster login next time.");
      
      // Continue to payment
      sessionStorage.setItem("patientData", JSON.stringify(pendingPatientData));
      setShowPasskeyPrompt(false);
      navigate("/payment");
      
    } catch (error) {
      clearTimeout(autoSkipTimeout);
      console.error("Passkey error:", error.name, error.message);
      
      let errorMessage = "Failed to register passkey.";
      
      if (error.name === 'NotAllowedError') {
        errorMessage = "Passkey registration was cancelled or denied";
      } else if (error.name === 'NotSupportedError') {
        errorMessage = "Passkeys not supported - check Brave settings";
      } else if (error.name === 'SecurityError') {
        errorMessage = "Security error occurred";
      } else if (error.name === 'InvalidStateError') {
        errorMessage = "A passkey already exists for this account";
      }
      
      toast.error(errorMessage + " Continuing without it.");
      
      sessionStorage.setItem("patientData", JSON.stringify(pendingPatientData));
      setShowPasskeyPrompt(false);
      navigate("/payment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipPasskey = () => {
    if (pendingPatientData) {
      sessionStorage.setItem("patientData", JSON.stringify(pendingPatientData));
    }
    setShowPasskeyPrompt(false);
    navigate("/payment");
  };

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    try {
      // Start login
      const startRes = await axios.post(`${API}/passkey/login/start`, {});
      const { challenge, rp_id, credential_ids } = startRes.data;
      
      // Get credential with more permissive options
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: base64urlToBuffer(challenge),
          rpId: rp_id,
          allowCredentials: credential_ids.map(id => ({
            type: "public-key",
            id: base64urlToBuffer(id),
            transports: ["internal", "hybrid", "usb", "ble", "nfc"]
          })),
          userVerification: "preferred",
          timeout: 120000
        }
      });
      
      if (!assertion) {
        throw new Error("No assertion returned");
      }
      
      // Finish login
      const finishRes = await axios.post(`${API}/passkey/login/finish`, {
        credential_id: bufferToBase64url(assertion.rawId),
        challenge: challenge,
        authenticator_data: bufferToBase64url(assertion.response.authenticatorData),
        signature: bufferToBase64url(assertion.response.signature)
      });
      
      const { patient_id, email: patientEmail, first_name, last_name, has_successful_payment, finger_ink_url } = finishRes.data;
      
      const patientData = {
        patient_id,
        first_name,
        last_name,
        email: patientEmail
      };
      
      sessionStorage.setItem("patientData", JSON.stringify(patientData));
      
      if (has_successful_payment && finger_ink_url) {
        // Already paid - go directly to success page with form access
        toast.success("Welcome back! Your payment is already complete.");
        sessionStorage.setItem("passkeyLoginPaid", "true");
        sessionStorage.setItem("fingerInkUrl", finger_ink_url);
        navigate("/payment-success?passkey=true");
      } else {
        // Need to pay
        toast.success("Welcome back! Please complete your payment.");
        navigate("/payment");
      }
      
    } catch (error) {
      console.error("Passkey login error:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Passkey login was cancelled");
      } else if (error.response?.status === 404) {
        toast.error("No passkey found. Please use another login method.");
      } else {
        toast.error("Passkey login failed. Please try another method.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="split-screen">
      {/* Left side - Image */}
      <div 
        className="split-image"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.1), rgba(15, 23, 42, 0.3)), url('https://images.unsplash.com/photo-1758691462814-485c3672e447?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjB3cml0aW5nJTIwcHJlc2NyaXB0aW9ufGVufDB8fHx8MTc2OTk1MjEzM3ww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="h-full flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-bold mb-3">{PRACTICE_NAME}</h2>
          <p className="text-lg text-white/80">Private Prescription Request Service</p>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="split-content bg-slate-50/50">
        <div className="w-full max-w-md animate-fade-in">
          {/* Trust badges */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            <div className="trust-badge">
              <ShieldCheck className="h-4 w-4 text-sky-500" />
              <span>Secure Portal</span>
            </div>
            <div className="trust-badge">
              <FileText className="h-4 w-4 text-sky-500" />
              <span>UK GDPR Compliant</span>
            </div>
          </div>
          
          <Card className="card-elevated" data-testid="start-card">
            <CardHeader className="text-center pb-2">
              <CardTitle className="heading-primary text-2xl sm:text-3xl">
                Prescription Request
              </CardTitle>
              <CardDescription className="text-body mt-2">
                Please verify your identity to continue
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-6">
              {/* Passkey login button */}
              {isPasskeySupported && (
                <div className="mb-6">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 border-slate-200 hover:bg-slate-50 hover:border-sky-300 transition-all"
                    onClick={handlePasskeyLogin}
                    disabled={isLoading}
                    data-testid="btn-passkey-login"
                  >
                    <Fingerprint className="h-5 w-5 mr-2 text-sky-500" />
                    Sign in with Passkey
                  </Button>
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-3 text-slate-500">Or verify your identity</span>
                    </div>
                  </div>
                </div>
              )}
              
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="details" data-testid="tab-details">
                    <User className="h-4 w-4 mr-2" />
                    Name & DOB
                  </TabsTrigger>
                  <TabsTrigger value="token" data-testid="tab-token">
                    <FileText className="h-4 w-4 mr-2" />
                    Patient ID
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="details">
                  <form onSubmit={handleDetailsSubmit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                          id="firstName"
                          data-testid="input-first-name"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="input-styled"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          data-testid="input-last-name"
                          placeholder="Smith"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="input-styled"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <div className="grid grid-cols-3 gap-2">
                        <select
                          data-testid="input-dob-day"
                          value={dobDay}
                          onChange={(e) => setDobDay(e.target.value)}
                          className="h-12 rounded-lg border border-slate-200 bg-slate-50/50 px-3 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                          disabled={isLoading}
                        >
                          <option value="">Day</option>
                          {[...Array(31)].map((_, i) => (
                            <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                          ))}
                        </select>
                        <select
                          data-testid="input-dob-month"
                          value={dobMonth}
                          onChange={(e) => setDobMonth(e.target.value)}
                          className="h-12 rounded-lg border border-slate-200 bg-slate-50/50 px-3 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                          disabled={isLoading}
                        >
                          <option value="">Month</option>
                          <option value="01">January</option>
                          <option value="02">February</option>
                          <option value="03">March</option>
                          <option value="04">April</option>
                          <option value="05">May</option>
                          <option value="06">June</option>
                          <option value="07">July</option>
                          <option value="08">August</option>
                          <option value="09">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                        <select
                          data-testid="input-dob-year"
                          value={dobYear}
                          onChange={(e) => setDobYear(e.target.value)}
                          className="h-12 rounded-lg border border-slate-200 bg-slate-50/50 px-3 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                          disabled={isLoading}
                        >
                          <option value="">Year</option>
                          {[...Array(100)].map((_, i) => {
                            const year = new Date().getFullYear() - i;
                            return <option key={year} value={year}>{year}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        data-testid="input-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-styled"
                        disabled={isLoading}
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full btn-primary mt-6"
                      disabled={isLoading}
                      data-testid="btn-verify-details"
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner mr-2" />
                          Verifying...
                        </>
                      ) : (
                        "Continue to Payment"
                      )}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="token">
                  <form onSubmit={handleTokenSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="token">Patient Identification Number</Label>
                      <Input
                        id="token"
                        data-testid="input-token"
                        placeholder="Enter your patient ID"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        className="input-styled"
                        disabled={isLoading}
                      />
                      <p className="text-xs text-slate-500">
                        Found in your Cliniko communication
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="tokenEmail">Email Address</Label>
                      <Input
                        id="tokenEmail"
                        data-testid="input-token-email"
                        type="email"
                        placeholder="you@example.com"
                        value={tokenEmail}
                        onChange={(e) => setTokenEmail(e.target.value)}
                        className="input-styled"
                        disabled={isLoading}
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full btn-primary mt-6"
                      disabled={isLoading}
                      data-testid="btn-verify-token"
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner mr-2" />
                          Verifying...
                        </>
                      ) : (
                        "Continue to Payment"
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          
          <p className="text-center text-xs text-slate-500 mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
          
          {/* Footer with contact info */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex flex-col items-center gap-2 text-sm text-slate-500">
              <p className="font-medium text-slate-700">{PRACTICE_NAME}</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href={`tel:${PRACTICE_PHONE}`} className="flex items-center gap-1 hover:text-sky-600 transition-colors">
                  <Phone className="h-3.5 w-3.5" />
                  {PRACTICE_PHONE}
                </a>
                <a href={`mailto:${PRACTICE_EMAIL}`} className="flex items-center gap-1 hover:text-sky-600 transition-colors">
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </a>
                <a href={PRACTICE_WEBSITE} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-sky-600 transition-colors">
                  <Globe className="h-3.5 w-3.5" />
                  Website
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Passkey Registration Prompt Dialog */}
      <Dialog open={showPasskeyPrompt} onOpenChange={setShowPasskeyPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-sky-500" />
              Save a Passkey?
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Create a passkey for faster, more secure logins in the future. Your device will verify your identity.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <Fingerprint className="h-5 w-5 text-sky-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-slate-900">Quick & Secure</p>
                <p className="text-slate-500">Use Face ID, Touch ID, or your device PIN</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleSkipPasskey}
                disabled={isLoading}
                data-testid="btn-skip-passkey"
              >
                Skip for now
              </Button>
              <Button
                className="flex-1 bg-sky-500 hover:bg-sky-600"
                onClick={handlePasskeyRegister}
                disabled={isLoading}
                data-testid="btn-create-passkey"
              >
                {isLoading ? (
                  <span className="spinner mr-2" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                Create Passkey
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
